import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Star, Home, CheckCircle2, ShieldCheck, ListChecks } from 'lucide-react';
import { Button } from '../ui/Button';
import { LegalAccordion } from '../ui/LegalAccordion';
interface HeroScreenProps {
  onStart: () => void;
}
export function HeroScreen({
  onStart
}: HeroScreenProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut'
      }
    }
  };
  return <div className="flex flex-col min-h-screen bg-white pb-3" data-id="element-25">
      {/* Trust Bar */}
      <motion.div initial={{
      opacity: 0,
      y: -12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.45
    }} className="bg-brand-beige py-2 px-3 flex items-center justify-center space-x-1.5 font-medium text-brand-dark" style={{
      fontSize: '13px'
    }} data-id="element-26">
        <div className="flex" data-id="element-27">
          {[...Array(4)].map((_, i) => <Star key={i} size={12} className="text-yellow-500" fill="currentColor" data-id="element-28" />)}
          <div className="relative" data-id="element-29">
            <Star size={12} className="text-gray-300" fill="currentColor" data-id="element-30" />
            <div className="absolute inset-0 overflow-hidden" style={{
            width: '80%'
          }} data-id="element-31">
              <Star size={12} className="text-yellow-500" fill="currentColor" data-id="element-32" />
            </div>
          </div>
        </div>
        <span data-id="element-33">4.8/5 — Padres que buscan más calma en casa</span>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex-1 flex flex-col px-5 pt-4 pb-3" data-id="element-34">
        {/* Logo */}
        <motion.div variants={itemVariants} className="flex items-center justify-center space-x-1.5 mb-4" data-id="element-35">
          <Home className="text-brand-blue" size={24} strokeWidth={1.5} data-id="element-36" />
          <h1 className="font-heading font-bold text-brand-dark tracking-tight" style={{
          fontSize: '22px',
          lineHeight: '1.2'
        }} data-id="element-37">
            Calma en Casa
          </h1>
        </motion.div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center text-center max-w-lg mx-auto w-full" data-id="element-38">
          <motion.span variants={itemVariants} className="inline-block py-1 px-3 rounded-full bg-blue-50 text-brand-blue font-semibold mb-4" style={{
          fontSize: '13px'
        }} data-id="element-39">
            Guía práctica para padres de niños intensos
          </motion.span>

          <motion.h2 variants={itemVariants} className="font-heading font-bold text-brand-dark mb-3 text-center mx-auto" style={{
          fontSize: '24px',
          lineHeight: '1.12',
          maxWidth: '320px'
        }} data-id="element-40">
            ¿Qué está dificultando la calma y concentración de tu hijo?
          </motion.h2>

          <motion.p variants={itemVariants} className="text-gray-600 mb-6 text-center mx-auto" style={{
          fontSize: '16px',
          lineHeight: '1.35',
          maxWidth: '330px'
        }} data-id="element-41">
            Responde este test de 3 minutos y descubre qué tipo de apoyo puede
            necesitar tu hijo en casa.
          </motion.p>

          <motion.div variants={itemVariants} className="w-full mb-2.5" data-id="element-42">
            <Button onClick={onStart} className="shadow-xl shadow-blue-200/50 !text-[17px] !min-h-[55px] !py-3.5" data-id="element-43">
              Comenzar test gratuito
            </Button>
          </motion.div>

          <motion.p variants={itemVariants} className="text-gray-400 mb-5 px-2 text-center" style={{
          fontSize: '12px',
          lineHeight: '1.4'
        }} data-id="element-44">
            Para padres de niños de 4 a 10 años. Orientación educativa, no
            diagnóstico médico.
          </motion.p>

          {/* Differentiation Line */}
          <motion.div variants={itemVariants} className="w-full mb-4" data-id="element-45">
            <div className="bg-brand-blue/10 border border-brand-blue/20 p-3 rounded-xl" data-id="element-46">
              <p className="text-brand-blue font-semibold text-center" style={{
              fontSize: '14px',
              lineHeight: '1.35'
            }} data-id="element-47">
                No es otra pantalla para tu hijo. Es una guía diaria para ti
                como padre.
              </p>
            </div>
          </motion.div>

          {/* Benefit Cards */}
          <motion.div variants={itemVariants} className="w-full space-y-2 mb-4 text-left" data-id="element-48">
            <div className="flex items-center space-x-3 text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100" data-id="element-49">
              <ListChecks className="text-brand-green shrink-0" size={20} data-id="element-50" />
              <span className="font-heading font-semibold" style={{
              fontSize: '16px'
            }} data-id="element-51">
                Herramientas prácticas
              </span>
            </div>
            <div className="flex items-center space-x-3 text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100" data-id="element-52">
              <ShieldCheck className="text-brand-lavender shrink-0" size={20} data-id="element-53" />
              <span className="font-heading font-semibold" style={{
              fontSize: '16px'
            }} data-id="element-54">
                Respuestas privadas
              </span>
            </div>
            <div className="flex items-center space-x-3 text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100" data-id="element-55">
              <CheckCircle2 className="text-brand-blue shrink-0" size={20} data-id="element-56" />
              <span className="font-heading font-semibold" style={{
              fontSize: '16px'
            }} data-id="element-57">
                Plan paso a paso
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Footer */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8,
      duration: 0.45
    }} className="px-5 mt-auto pb-3" data-id="element-58">
        <LegalAccordion variant="light" data-id="element-59" />
      </motion.div>
    </div>;
}